import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Mic, MicOff, Video, VideoOff, Settings, Users, Brain, Database, Sparkles, MessageCircle, Clock, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLiveKit } from '../hooks/useLiveKit';
import LiveKitChatRoom from '../components/LiveKitChatRoom';
import ModernVoiceAssistant from '../components/ModernVoiceAssistant';
import { logger } from '../utils/logger';
import toast from 'react-hot-toast';

interface LivePersonalChatProps {
  conversationId?: string;
}

interface ChartType {
  name: string;
  category: string;
  description: string;
  keywords: string[];
  priority: number;
}

interface PineconeSearchResult {
  success: boolean;
  results: Array<{
    id: string;
    score: number;
    metadata: {
      chartType: string;
      content: string;
      userId: string;
    };
  }>;
  total_found: number;
  chart_types_found: string[];
  error?: string;
}

interface FunctionCallResult {
  success: boolean;
  response: string;
  chartData?: any;
  confidence?: number;
  sources?: string[];
  error?: string;
}

// Chart types configuration (matching the enhanced agent)
const CHART_TYPES: Record<string, ChartType> = {
  'astro_details': {
    name: 'astro_details',
    category: 'personality',
    description: 'Basic astrological details including sun sign, moon sign, ascendant, nakshatra',
    keywords: ['personality', 'basic', 'sun', 'moon', 'ascendant', 'nakshatra'],
    priority: 1
  },
  'planets': {
    name: 'planets',
    category: 'personality',
    description: 'Planetary positions in signs and houses',
    keywords: ['planets', 'positions', 'signs', 'houses'],
    priority: 1
  },
  'vedic_horoscope': {
    name: 'vedic_horoscope',
    category: 'personality',
    description: 'Complete Vedic horoscope chart',
    keywords: ['horoscope', 'vedic', 'complete', 'chart'],
    priority: 1
  },
  'current_vdasha': {
    name: 'current_vdasha',
    category: 'timing',
    description: 'Current Vimshottari Dasha period',
    keywords: ['current', 'dasha', 'vimshottari', 'timing'],
    priority: 1
  },
  'major_vdasha': {
    name: 'major_vdasha',
    category: 'timing',
    description: 'Major Vimshottari Dasha periods',
    keywords: ['major', 'dasha', 'vimshottari'],
    priority: 2
  },
  'advanced_panchang': {
    name: 'advanced_panchang',
    category: 'timing',
    description: 'Advanced Panchang details',
    keywords: ['panchang', 'advanced', 'timing'],
    priority: 1
  },
  'basic_gem_suggestion': {
    name: 'basic_gem_suggestion',
    category: 'remedies',
    description: 'Gemstone recommendations',
    keywords: ['gemstones', 'remedies', 'suggestions'],
    priority: 2
  }
};

const LivePersonalChat: React.FC<LivePersonalChatProps> = ({ conversationId: propConversationId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Get conversationId from props, URL params, or generate new one
  const urlParams = new URLSearchParams(location.search);
  const conversationId = propConversationId || 
                        urlParams.get('conversationId') || 
                        `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // State
  const [enableVoice, setEnableVoice] = useState(false);
  const [enableVideo, setEnableVideo] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showFunctionCalls, setShowFunctionCalls] = useState(false);
  const [activeFunctionCalls, setActiveFunctionCalls] = useState<FunctionCallResult[]>([]);
  const [chartData, setChartData] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [useVoiceAssistant, setUseVoiceAssistant] = useState(true);
  const [skipConnection, setSkipConnection] = useState(false);

  // LiveKit hook
  const {
    isConnecting,
    isConnected,
    connectionError,
    roomToken,
    roomName,
    createPersonalChatRoom,
    disconnect,
    healthCheck,
    resetConnectionState,
  } = useLiveKit({
    enableVoice,
    enableVideo,
    onConnect: () => {
      logger.info('Connected to enhanced personal chat room');
      setIsInitializing(false);
    },
    onDisconnect: () => {
      logger.info('Disconnected from enhanced personal chat room');
    },
    onError: (error) => {
      logger.error('LiveKit connection error:', error);
      setIsInitializing(false);
    },
  });

  // Pinecone RAG service functions
  const selectRelevantCharts = useCallback(async (query: string): Promise<string[]> => {
    const queryLower = query.toLowerCase();
    const selectedCharts: string[] = [];
    
    // Personality analysis
    if (anyWordInQuery(queryLower, ['personality', 'character', 'traits', 'nature', 'behavior', 'temperament'])) {
      selectedCharts.push('astro_details', 'planets', 'vedic_horoscope');
    }
    
    // Career analysis
    if (anyWordInQuery(queryLower, ['career', 'job', 'work', 'profession', 'business', 'success', 'ambition'])) {
      selectedCharts.push('astro_details', 'planets', 'current_vdasha', 'major_vdasha');
    }
    
    // Relationship analysis
    if (anyWordInQuery(queryLower, ['relationship', 'love', 'marriage', 'compatibility', 'partner', 'romance'])) {
      selectedCharts.push('astro_details', 'planets', 'vedic_horoscope', 'current_vdasha');
    }
    
    // Timing analysis
    if (anyWordInQuery(queryLower, ['timing', 'when', 'period', 'dasha', 'transit', 'auspicious'])) {
      selectedCharts.push('current_vdasha', 'major_vdasha', 'advanced_panchang');
    }
    
    // Health analysis
    if (anyWordInQuery(queryLower, ['health', 'wellness', 'healing', 'remedies', 'gemstone'])) {
      selectedCharts.push('astro_details', 'planets', 'basic_gem_suggestion', 'current_vdasha');
    }
    
    // Default to comprehensive analysis
    if (selectedCharts.length === 0) {
      selectedCharts.push('astro_details', 'planets', 'current_vdasha', 'vedic_horoscope');
    }
    
    // Remove duplicates and limit to top 5
    return [...new Set(selectedCharts)].slice(0, 5);
  }, []);

  const anyWordInQuery = (query: string, words: string[]): boolean => {
    return words.some(word => query.includes(word));
  };

  const searchPineconeCharts = useCallback(async (query: string, chartTypes: string[]): Promise<PineconeSearchResult> => {
    try {
      const response = await fetch('/api/v1/pinecone/pinecone-search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          query,
          chartTypes,
          userId: user?.uid,
          maxResults: 5
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Pinecone search error:', error);
      return {
        success: false,
        results: [],
        total_found: 0,
        chart_types_found: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }, [user?.uid]);

  const analyzeWithBackend = useCallback(async (query: string, chartData: any): Promise<FunctionCallResult> => {
    try {
      const response = await fetch('/api/v1/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          query,
          userContext: {
            userId: user?.uid,
            chartData,
            analysisType: 'enhanced_live_chat'
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return {
        success: result.success,
        response: result.response,
        chartData: result.chartData,
        confidence: result.confidence,
        sources: result.sources
      };
    } catch (error) {
      logger.error('Backend analysis error:', error);
      return {
        success: false,
        response: 'Analysis failed. Please try again.',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }, [user?.uid]);

  // Enhanced function calling workflow
  const processQueryWithFunctionCalling = useCallback(async (query: string) => {
    if (!user?.uid) return;

    setIsAnalyzing(true);
    const functionCalls: FunctionCallResult[] = [];

    try {
      // Step 1: Select relevant charts
      const selectedCharts = await selectRelevantCharts(query);
      functionCalls.push({
        success: true,
        response: `Selected ${selectedCharts.length} relevant chart types: ${selectedCharts.join(', ')}`,
        sources: ['Chart Selection']
      });
      setActiveFunctionCalls([...functionCalls]);

      // Step 2: Search Pinecone
      const searchResult = await searchPineconeCharts(query, selectedCharts);
      functionCalls.push({
        success: searchResult.success,
        response: searchResult.success 
          ? `Found ${searchResult.total_found} relevant chart documents from Pinecone`
          : `Pinecone search failed: ${searchResult.error}`,
        sources: ['Pinecone RAG Search']
      });
      setActiveFunctionCalls([...functionCalls]);

      if (!searchResult.success || searchResult.results.length === 0) {
        functionCalls.push({
          success: false,
          response: 'No relevant chart data found. Please ensure your charts are processed.',
          sources: ['Error Handling']
        });
        setActiveFunctionCalls([...functionCalls]);
        return;
      }

      // Step 3: Analyze with backend
      const analysisResult = await analyzeWithBackend(query, searchResult.results);
      functionCalls.push({
        success: analysisResult.success,
        response: analysisResult.response,
        confidence: analysisResult.confidence,
        sources: analysisResult.sources
      });
      setActiveFunctionCalls([...functionCalls]);

      // Store chart data for display
      setChartData(searchResult.results);

    } catch (error) {
      logger.error('Function calling workflow error:', error);
      functionCalls.push({
        success: false,
        response: 'Function calling workflow failed. Please try again.',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      setActiveFunctionCalls([...functionCalls]);
    } finally {
      setIsAnalyzing(false);
    }
  }, [user?.uid, selectRelevantCharts, searchPineconeCharts, analyzeWithBackend]);

  // Initialize room connection
  const initializeRoom = useCallback(async () => {
    if (!user) {
      toast.error('Please sign in to start a live chat');
      navigate('/login');
      return;
    }

    if (isConnecting || isConnected || isInitializing) {
      logger.info('Already initializing or connected, skipping duplicate initialization');
      return;
    }

    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication required. Please log in again.');
      navigate('/login');
      return;
    }

    setIsInitializing(true);
    
    try {
      const isHealthy = await healthCheck();
      if (!isHealthy) {
        toast.error('Live chat service is currently unavailable. Please try again later.');
        setIsInitializing(false);
        return;
      }

      const success = await createPersonalChatRoom(conversationId);
      if (!success) {
        setIsInitializing(false);
      }
    } catch (error) {
      logger.error('Error during room initialization:', error);
      setIsInitializing(false);
      toast.error('Failed to initialize live chat. Please try again.');
    }
  }, [user, conversationId, createPersonalChatRoom, healthCheck, navigate, isConnecting, isConnected, isInitializing]);

  // Initialize on mount with timeout
  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;
    
    const init = async () => {
      if (isMounted && !isConnected && !isConnecting && !isInitializing) {
        await initializeRoom();
      }
    };
    
    const timer = setTimeout(init, 100);
    
    // Set a timeout to prevent infinite loading
    timeoutId = setTimeout(() => {
      if (isMounted && isInitializing) {
        logger.warn('Live chat initialization timed out');
        setIsInitializing(false);
        toast.error('Connection timed out. Please try again.');
      }
    }, 30000); // 30 second timeout
    
    return () => {
      isMounted = false;
      clearTimeout(timer);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isInitializing]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (isConnected) {
      disconnect();
    }
    navigate('/personal-chat');
  }, [isConnected, disconnect, navigate]);

  // Toggle voice
  const toggleVoice = useCallback(() => {
    setEnableVoice(prev => {
      const newValue = !prev;
      toast.success(newValue ? 'Voice enabled' : 'Voice disabled');
      return newValue;
    });
  }, []);

  // Toggle video
  const toggleVideo = useCallback(() => {
    setEnableVideo(prev => {
      const newValue = !prev;
      toast.success(newValue ? 'Video enabled' : 'Video disabled');
      return newValue;
    });
  }, []);

  // Manual retry function
  const manualRetry = useCallback(() => {
    resetConnectionState();
    setIsInitializing(false);
    setTimeout(() => {
      initializeRoom();
    }, 500);
  }, [initializeRoom, resetConnectionState]);

  // Test function calling
  const testFunctionCalling = useCallback(async () => {
    const testQuery = "What's my personality like?";
    await processQueryWithFunctionCalling(testQuery);
  }, [processQueryWithFunctionCalling]);

  // Render loading state
  if (isInitializing || isConnecting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {isInitializing ? 'Initializing Enhanced Live Chat...' : 'Connecting to Room...'}
            </h2>
            <p className="text-gray-600">
              Setting up your AI-powered astrology chat with Pinecone RAG
            </p>
            
            {isInitializing && (
              <div className="mt-4 space-y-2 text-sm text-gray-500">
                <div className="flex items-center justify-center space-x-2">
                  <div className="h-2 w-2 bg-purple-600 rounded-full animate-pulse"></div>
                  <span>Initializing Pinecone RAG...</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <div className="h-2 w-2 bg-purple-600 rounded-full animate-pulse animation-delay-200"></div>
                  <span>Setting up function calling...</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <div className="h-2 w-2 bg-purple-600 rounded-full animate-pulse animation-delay-400"></div>
                  <span>Preparing enhanced chat room...</span>
                </div>
              </div>
            )}
            
            <div className="mt-6 space-y-3">
              <div className="flex justify-center space-x-2">
                <button
                  onClick={manualRetry}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                >
                  Retry Connection
                </button>
                <button
                  onClick={handleBack}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Back to Chat
                </button>
              </div>
              <button
                onClick={() => {
                  setIsInitializing(false);
                  setSkipConnection(true);
                  toast.success('Skipped connection. You can still use chat features.');
                }}
                className="w-full px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
              >
                Skip Connection & Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (connectionError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-600 text-xl">⚠️</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Connection Failed
            </h2>
            <p className="text-gray-600 mb-4">
              {connectionError}
            </p>
            
            <div className="space-y-3">
              <button
                onClick={initializeRoom}
                className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
              >
                Try Again
              </button>
              <button
                onClick={handleBack}
                className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Back to Regular Chat
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render skipped connection state
  if (skipConnection && !isInitializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Brain className="h-5 w-5 mr-2 text-purple-600" />
                    Live Personal Chat (Offline Mode)
                  </h1>
                  <p className="text-sm text-gray-600">
                    AI-powered astrology chat • Connection skipped
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSkipConnection(false);
                  setIsInitializing(true);
                  setTimeout(() => initializeRoom(), 100);
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Try Connect Again
              </button>
            </div>
          </div>
        </div>

        {/* Simple Chat Interface */}
        <div className="flex-1 h-[calc(100vh-4rem)] flex flex-col">
          <div className="flex-1 p-4">
            <div className="max-w-4xl mx-auto h-full bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
                <h2 className="text-lg font-semibold text-gray-900">Chat with Rraasi AI</h2>
                <p className="text-sm text-gray-600">Ask about your astrology, personality, or get guidance</p>
              </div>

              {/* Messages Area */}
              <div className="flex-1 p-4 overflow-y-auto">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                      <Brain className="h-4 w-4 text-white" />
                    </div>
                    <div className="bg-gray-100 rounded-lg p-3 max-w-xs">
                      <p className="text-sm text-gray-800">
                        Hello! I'm Rraasi, your AI astrology assistant. Even though we're in offline mode, 
                        I can still help you with astrology questions and insights. What would you like to know?
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Input Area */}
              <div className="p-4 border-t border-gray-200">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Ask about your astrology, personality, or get guidance..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    onClick={() => toast('Live connection required for real-time chat. Please try connecting again.')}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    Send
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 For real-time chat with the AI agent, please try connecting again using the button above.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render connected state
  if (isConnected && roomToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Left side */}
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleBack}
                  className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                
                <div>
                  <h1 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Brain className="h-5 w-5 mr-2 text-purple-600" />
                    Live Personal Chat
                  </h1>
                  <p className="text-sm text-gray-600">
                    AI-powered astrology with Pinecone RAG • Room: {roomName}
                  </p>
                </div>
              </div>

              {/* Right side - Controls */}
              <div className="flex items-center space-x-2">
                {/* Mode Switch Button */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setUseVoiceAssistant(false)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !useVoiceAssistant
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  >
                    💬 Chat
                  </button>
                  <button
                    onClick={() => setUseVoiceAssistant(true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      useVoiceAssistant
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  >
                    🎤 Voice
                  </button>
                </div>

                {/* Function calls toggle */}
                <button
                  onClick={() => setShowFunctionCalls(!showFunctionCalls)}
                  className={`p-2 rounded-md ${
                    showFunctionCalls
                      ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  title="Toggle Function Calls"
                >
                  <Database className="h-5 w-5" />
                </button>

                {/* Test function calling */}
                <button
                  onClick={testFunctionCalling}
                  disabled={isAnalyzing}
                  className="p-2 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Test Function Calling"
                >
                  <Sparkles className="h-5 w-5" />
                </button>

                {/* Voice toggle */}
                <button
                  onClick={toggleVoice}
                  className={`p-2 rounded-md ${
                    enableVoice
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  title={enableVoice ? 'Disable Voice' : 'Enable Voice'}
                >
                  {enableVoice ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
                </button>

                {/* Video toggle */}
                <button
                  onClick={toggleVideo}
                  className={`p-2 rounded-md ${
                    enableVideo
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  title={enableVideo ? 'Disable Video' : 'Enable Video'}
                >
                  {enableVideo ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
                </button>

                {/* Settings */}
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  title="Settings"
                >
                  <Settings className="h-5 w-5" />
                </button>

                {/* Connection status */}
                <div className="flex items-center space-x-2 px-3 py-1 bg-green-100 text-green-800 rounded-full">
                  <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-sm font-medium">Live</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Function Calls Panel */}
        {showFunctionCalls && (
          <div className="bg-white border-b border-gray-200 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Database className="h-5 w-5 mr-2 text-purple-600" />
                  Function Calls & Pinecone RAG
                </h3>
                <div className="flex items-center space-x-2">
                  {isAnalyzing && (
                    <div className="flex items-center text-blue-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      <span className="text-sm">Analyzing...</span>
                    </div>
                  )}
                  <button
                    onClick={testFunctionCalling}
                    disabled={isAnalyzing}
                    className="px-3 py-1 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 disabled:opacity-50"
                  >
                    Test Function Calling
                  </button>
                </div>
              </div>

              {/* Function calls list */}
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {activeFunctionCalls.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <MessageCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No function calls yet. Ask a question to see the AI's reasoning process!</p>
                  </div>
                ) : (
                  activeFunctionCalls.map((call, index) => (
                    <div key={index} className={`p-4 rounded-lg border ${
                      call.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <div className={`h-2 w-2 rounded-full mr-2 ${
                              call.success ? 'bg-green-500' : 'bg-red-500'
                            }`}></div>
                            <span className="text-sm font-medium text-gray-900">
                              Step {index + 1}: {call.sources?.[0] || 'Function Call'}
                            </span>
                            {call.confidence && (
                              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                {Math.round(call.confidence * 100)}% confidence
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700">{call.response}</p>
                          {call.sources && call.sources.length > 1 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {call.sources.slice(1).map((source, i) => (
                                <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                  {source}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Chart data display */}
              {chartData && chartData.length > 0 && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center">
                    <Star className="h-4 w-4 mr-1" />
                    Retrieved Chart Data ({chartData.length} documents)
                  </h4>
                  <div className="space-y-2">
                    {chartData.slice(0, 3).map((result: any, index: number) => (
                      <div key={index} className="text-xs text-blue-800">
                        <span className="font-medium">{result.metadata?.chartType || 'Unknown'}</span>
                        <span className="ml-2">(Score: {result.score?.toFixed(2)})</span>
                      </div>
                    ))}
                    {chartData.length > 3 && (
                      <div className="text-xs text-blue-600">
                        ... and {chartData.length - 3} more documents
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-white border-b border-gray-200 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Audio Settings</h3>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={enableVoice}
                        onChange={toggleVoice}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Enable voice chat</span>
                    </label>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Video Settings</h3>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={enableVideo}
                        onChange={toggleVideo}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Enable video chat</span>
                    </label>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">AI Features</h3>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={showFunctionCalls}
                        onChange={() => setShowFunctionCalls(!showFunctionCalls)}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Show function calls</span>
                    </label>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Room Info</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div>Room: {roomName}</div>
                    <div>Type: Enhanced Personal Chat</div>
                    <div>Status: Connected</div>
                    <div>Features: Pinecone RAG + Function Calling</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 h-[calc(100vh-4rem)]">
          {useVoiceAssistant ? (
            <ModernVoiceAssistant
              roomName={roomToken.roomName}
              token={roomToken.token}
              wsUrl={roomToken.wsUrl}
              onDisconnect={disconnect}
              className="h-full"
            />
          ) : (
            <div className="h-full p-4">
              <div className="h-full max-w-7xl mx-auto">
                <LiveKitChatRoom
                  roomName={roomToken.roomName}
                  token={roomToken.token}
                  wsUrl={roomToken.wsUrl}
                  onDisconnect={disconnect}
                  enableVoice={enableVoice}
                  enableVideo={enableVideo}
                  className="h-full"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Fallback loading state
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
    </div>
  );
};

export default LivePersonalChat;
